/**
 * Gestion de documents (migrations 0261-0262) — hooks React Query au-dessus du
 * client partagé `@jeraisonne/documents` (le client Supabase y est INJECTÉ).
 *
 * Écriture directe PostgREST + Storage sous RLS, sans edge function : la table
 * `documents` est postérieure à `types.generated.ts` (stale volontaire, cf.
 * CLAUDE.md) → client générique, cast `as unknown as SupabaseClient`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createDocumentsClient,
  type DocumentRow,
  type DocumentTarget,
} from '@jeraisonne/documents';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export const documentsClient = createDocumentsClient(supabase as unknown as SupabaseClient);

/** Clé stable d'une cible (sert aux clés de cache React Query). */
export function documentTargetKey(target: DocumentTarget): string {
  switch (target.kind) {
    case 'contact':
      return `contact:${target.peerId}`;
    case 'class':
      return `class:${target.classId}`;
    case 'room':
      return `room:${target.roomId}`;
    case 'discussion':
      return `discussion:${target.discussionId}`;
  }
}

/** Fichiers d'UNE cible (fiche contact, classe, réunion). */
export function useDocuments(target: DocumentTarget | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['documents', 'list', target ? documentTargetKey(target) : null, user?.id],
    enabled: !!target && !!user?.id,
    staleTime: 30_000,
    queryFn: () => documentsClient.list(target as DocumentTarget, user?.id as string),
  });
}

/** Tout ce que la RLS laisse voir à l'utilisateur (page « Mes fichiers »). */
export function useVisibleDocuments() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['documents', 'visible', user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    queryFn: () => documentsClient.listVisible(),
  });
}

export function useUploadDocument() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      target,
    }: {
      file: File;
      target: DocumentTarget;
    }): Promise<DocumentRow> => {
      if (!user?.id) throw new Error('Session expirée.');
      return documentsClient.upload({ file, target, userId: user.id });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (doc: Pick<DocumentRow, 'id' | 'storagePath'>) => documentsClient.remove(doc),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

/** URL signée d'une miniature (images seulement — les autres se signent au clic). */
export function useDocumentThumbnailUrl(storagePath: string, enabled: boolean) {
  return useQuery({
    queryKey: ['documents', 'thumb', storagePath],
    enabled,
    staleTime: 30 * 60_000,
    queryFn: () => documentsClient.signedUrl(storagePath, { ttlSeconds: 3600 }),
  });
}
